/**
 * The view of the ratings across every specialist, as the address holds it.
 * It opens on the low ones (three or under), the shown and the hidden alike:
 * what somebody has to look at.
 */
import { pageOf } from "@/shared/lib/search";

import type { RatingsFilter, RatingsShown } from "../api/queries";
import { RATINGS_SHOWN } from "./person";

/** The scores a list can be cut at: this score or lower. */
export const MAX_SCORES = [1, 2, 3, 4, 5] as const;
export type MaxScore = (typeof MAX_SCORES)[number];

/** What the page opens on: the low ratings. */
export const DEFAULT_MAX_SCORE: MaxScore = 3;

export interface RatingsSearch {
  /** Ratings of this score or lower; `5` is every rating. Left out, the low ones. */
  max?: MaxScore;
  shown?: RatingsShown;
  page?: number;
}

export function parseRatingsSearch(raw: Readonly<Record<string, unknown>>): RatingsSearch {
  const search: RatingsSearch = {};
  const max = MAX_SCORES.find((known) => known === Number(raw["max"]));
  if (max !== undefined && max !== DEFAULT_MAX_SCORE) search.max = max;
  const shown = RATINGS_SHOWN.find((known) => known === raw["shown"]);
  if (shown !== undefined) search.shown = shown;
  const page = pageOf(raw["page"]);
  if (page !== undefined) search.page = page;
  return search;
}

/** What `GET /v2/admin/ratings` is asked: every score needs no cut at all. */
export function ratingsFilter(search: RatingsSearch): RatingsFilter {
  const filter: RatingsFilter = {};
  const max = search.max ?? DEFAULT_MAX_SCORE;
  if (max < 5) filter.max_score = max;
  if (search.shown !== undefined) filter.hidden = search.shown === "hidden";
  return filter;
}
